import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { api } from '../api.js';
import { EventTask } from '../contexts.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { InputWithStatus } from './input-with-status.js';

export type ProjectSlugPicked = {
  orgSlug: string,
  projectSlug: string,
}

@customElement('pick-project-name')
export class PickProjectName extends LitElement {

  @property({ attribute: false }) orgSlug?: string;
  // Pre-fills the input. No special validation semantics.
  @property({ attribute: false }) initial?: string;
  // Optional: the project's currently saved name in a rename flow. When the
  // typed value matches this case-insensitively, the existence check against
  // the server is skipped (the name is already yours). Do NOT pass this from
  // a "new project" flow — `initial` there is just an auto-filled suggestion
  // and collisions still need to be detected.
  @property({ attribute: false }) existing?: string;
  @property({ type: Boolean }) disabled?: boolean;

  @query('input-with-status') private _input?: InputWithStatus;

  private _validateProjectSlug = new EventTask(this, 'project-slug', {
    autoRun: false,
    args: () => [this.orgSlug, this._input?.value()] as const,
    task: async ([orgSlug, projectSlug], { signal }): Promise<ProjectSlugPicked> => {
      CHECK(orgSlug && projectSlug !== undefined);

      if (!projectSlug.trim().length) {
        this._input?.setStatus(false, 'Project name must not be blank');
      } else if (!(/^[.0-9a-z_-]+$/i).test(projectSlug)) {
        this._input?.setStatus(false, 'The project name can only contain ASCII letters, digits, and the characters ., -, and _.');
      } else if (this.existing && projectSlug.toLowerCase() === this.existing.toLowerCase()) {
        this._input?.setStatus(true, `"${projectSlug}" is available`);
      } else {
        // Small cool-down to not spam server with requests.
        await new Promise(x => setTimeout(x, 200));
        signal.throwIfAborted();
        const projectExists = await api.project.checkProjectExists.GET({ orgSlug, projectSlug }).catch(e => false);
        if (projectExists) {
          this._input?.setStatus(false, `"${projectSlug}" is already taken`);
        } else {
          this._input?.setStatus(true, `"${projectSlug}" is available`);
        }
      }
      CHECK(this._input?.success());
      return { projectSlug, orgSlug };
    }
  });

  setStatus(success: boolean, message: string) {
    this._input?.setStatus(success, message);
  }

  // Trigger validation explicitly — used when the project name is set
  // programmatically (e.g. auto-filled from a selected GitHub repo) so the
  // parent doesn't have to wait for the user to type to enable submit.
  // Awaits the inner input chain so the task reads the propagated value
  // rather than an empty one (which would briefly flash "must not be blank").
  async validate() {
    await this.updateComplete;
    await this._input?.updateComplete;
    await this._input?.input()?.updateComplete;
    this._validateProjectSlug.run();
  }

  success(): boolean {
    return this._input?.success() ?? false;
  }

  value(): string {
    return this._input?.value()?.trim() ?? '';
  }

  override render() {
    return html`
      <input-with-status
        initial=${this.initial}
        placeholder="Project name"
        autocomplete=off
        ?disabled=${this.disabled}
        @sl-input=${() => this._validateProjectSlug.run()}
      >
        <slot></slot>
      </input-with-status>
    `;
  }

  static styles = [css`
  `];
}
